import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { formatPrice } from '@dashboard/services/formatting';
import getSubscriptionIntervalWithSlash from '@dashboard/services/formatting/getSubscriptionIntervalWithSlash';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import { fetchInfinite } from '@dashboard/services/request';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import { Checkbox, FormElement, Label, Select } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Lozenge from '@dashboard/components/Lozenge';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';

import Table from '../components/Table';

const Subscriptions = () => {
  const { token } = useAuth();
  const history = useHistory();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [livemode, setLivemode] = useState(query.livemode || true);
  const [status, setStatus] = useState(query.status || 'all');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        livemode,
        status,
      }),
    });
  }, [sortBy, sortDirection, livemode, status]);

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['subscriptions', sortBy, sortDirection, livemode, status],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/subscriptions?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          ...(livemode !== undefined ? { livemode } : {}),
          ...(status !== 'all' ? { status } : {}),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <div className="pr-4">
      <div className="flex items-end justify-end gap-x-2">
        <FormElement className="!mb-0">
          <div className="flex items-center gap-x-2 w-full">
            <Checkbox
              id="livemode"
              value={!livemode}
              checked={!livemode}
              onChange={() => setLivemode(livemode ? undefined : true)}
            />

            <Label isInline htmlFor="livemode">
              Include test payments
            </Label>
          </div>
        </FormElement>

        <FormElement className="!mb-0">
          <Label>Status</Label>
          <Select value={status} onChange={e => setStatus(e.target.value)} className="w-32">
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="failed">Failed</option>
            <option value="incomplete">Pending</option>
            <option value="abandoned">Abandoned</option>
          </Select>
        </FormElement>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th
            onSort={onSort}
            sortKey="page.title"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Checkout
          </Table.Th>

          <Table.Th>Type</Table.Th>

          <Table.Th onSort={onSort} sortKey="amount" sortBy={sortBy} sortDirection={sortDirection}>
            Amount
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="seller.displayName"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Merchant
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="status" sortBy={sortBy} sortDirection={sortDirection}>
            Status
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(subscription => (
                <Table.Row key={subscription.id}>
                  <Table.Td>
                    <Link href={`/pages/${subscription.page._id}/customize`}>
                      {subscription.product.title}
                    </Link>

                    {!subscription.livemode && (
                      <Tooltip label={subscription.paymentError} side="right">
                        <StatusBadge status="test" withInfoIcon={!!subscription.paymentError}>
                          Test
                        </StatusBadge>
                      </Tooltip>
                    )}
                  </Table.Td>

                  <Table.Td>Checkout</Table.Td>

                  <Table.Td>
                    {formatPrice({
                      amount: subscription.amount,
                      currency: subscription.currency,
                    })}{' '}
                    {getSubscriptionIntervalWithSlash(
                      subscription.interval,
                      subscription.intervalCount,
                      subscription.page,
                    )}
                  </Table.Td>
                  <Table.Td className="relative">
                    <Link href={`/admin/merchants/${subscription.seller?._id}`}>
                      {subscription.seller?.displayName || subscription.seller?.name}
                    </Link>

                    {subscription.seller?.disabled && (
                      <Lozenge className="ml-2 absolute right-0 mt-0.5">Disabled</Lozenge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {format(new Date(subscription.createdAt), 'd MMM, yyyy, HH:mm')}
                  </Table.Td>
                  <Table.Td className="space-x-2">
                    <Tooltip label={subscription.paymentError} side="right">
                      <StatusBadge
                        status={subscription.status}
                        withInfoIcon={!!subscription.paymentError}
                      />
                    </Tooltip>

                    <Button
                      to={`${getCheckoutUrl(subscription.page, subscription.seller)}`}
                      appearance="light"
                      target="_blank"
                      size="small"
                    >
                      View checkout
                    </Button>
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </div>
  );
};

export default Subscriptions;
